import { useMutation } from '@apollo/client';
import { CREATE_CREATIVE_PACKAGE } from '../../graphql/mutations/creativeMutations';

const sanitizeInput = (input = {}) => {
  const cleaned = { ...input };
  Object.keys(cleaned).forEach((key) => {
    if (cleaned[key] === undefined || cleaned[key] === null) {
      delete cleaned[key];
    }
  });
  return cleaned;
};

export const buildCreateCreativePackageInput = (packageData) => sanitizeInput({
  id: packageData?.id,
  creativeID: packageData?.creativeID,
  serviceType: packageData?.serviceType,
  name: packageData?.name,
  description: packageData?.description,
  packageType: packageData?.packageType,
  duration: packageData?.duration,
  price: packageData?.price,
  pricePerHour: packageData?.pricePerHour,
  deliverables: packageData?.deliverables, // Array of strings
  includedServices: packageData?.includedServices, // Array of strings
  deliveryTime: packageData?.deliveryTime,
  portfolioImages: packageData?.portfolioImages, // Array of strings (S3 keys)
  equipment: packageData?.equipment, // Array of strings
  isPopular: packageData?.isPopular || false,
  order: packageData?.order,
  bookingCount: packageData?.bookingCount || 0,
  averageRating: packageData?.averageRating,
  isActive: packageData?.isActive !== undefined ? packageData.isActive : true,
  status: packageData?.status || 'DRAFT'
});

/**
 * Hook to create a new Creative Package
 * @returns {object} - { handleCreatePackage, loading, error }
 */
export const useCreateCreativePackage = () => {
  const [createPackageMutation, { loading, error }] = useMutation(CREATE_CREATIVE_PACKAGE, {
    refetchQueries: ['CreativePackagesByCreativeID', 'GetCreative'],
    awaitRefetchQueries: true
  });

  const handleCreatePackage = async (packageData) => {
    try {
      if (!packageData?.creativeID) {
        throw new Error('Das Creative-Profil konnte nicht zugeordnet werden. Bitte lade die Seite neu und versuche es erneut.');
      }

      if (!packageData?.serviceType) {
        throw new Error('Bitte wähle einen Servicetyp aus.');
      }

      if (!packageData?.name) {
        throw new Error('Bitte gib einen Paketnamen ein.');
      }

      if (!packageData?.packageType) {
        throw new Error('Bitte wähle einen Pakettyp aus.');
      }

      if (packageData?.duration === undefined || packageData?.duration === null) {
        throw new Error('Bitte gib eine Dauer ein.');
      }

      if (packageData?.price === undefined || packageData?.price === null) {
        throw new Error('Bitte gib einen Preis ein.');
      }

      const input = buildCreateCreativePackageInput(packageData);

      const variables = { input };

      const { data: packageResponse } = await createPackageMutation({
        variables
      });

      return packageResponse?.createCreativePackage;
    } catch (error) {
      console.error('Error creating creative package:', error);
      throw error;
    }
  };

  return {
    handleCreatePackage,
    loading,
    error
  };
};
